import React, { lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import GlobalToast from '../global-toast.coffee'
import {UIRouter, UIView, hashLocationPlugin, ArrayType} from '@uirouter/react'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import theme from '../theme.coffee'
import moment from 'moment'
import TitleBar from './title_bar.coffee'
import Application from '../loopback_api.coffee'
import ErrorBoundary from '../error_boundary.coffee'
import {isUnauthenticatedDeepLink} from '../Login/unauthenticated_routes.coffee'
import { LoadingScreen as Loading, hide_agent_widget } from '../common.coffee'
import axios from 'axios'
import ClientAPI from  "../../../../../schema/framework/shared/client_api.coffee"
import {i18nCreateElement, initI18n, TranslationProvider, I18nRegistry, t} from '../../../../../schema/framework/shared/i18n/index.coffee'

# Normalize route by removing student IDs but keeping game IDs
# This is el-feet specific routing logic
normalizeRoute = (route) ->
  return route unless route?
  # Remove studentId from /play/:gameId/:studentId routes
  # Keep the gameId since each game has different strings
  route.replace(/^(#?\/play\/[0-9a-fA-F]{24})\/[0-9a-fA-F]{24}/, '$1')

standardComponent = (importPromise) ->
  LazyComponent = lazy(() => importPromise)
  (props) ->
    <Suspense fallback={<Loading/>}>
      <LazyComponent {...props} />
    </Suspense>

# Institutional sign-in, NOT the parent screen. The parent flow emails a
# generated password on request, which is designed behaviour for a parent on a
# child's device and the wrong thing entirely for a login holding roster and
# student data. Card 6a639e22893d0bbbaaacec81.
Login              = standardComponent import('../Login/staff_login.coffee')
ResetPassword      = standardComponent import('../Login/reset_password.coffee')
Help               = standardComponent import('../Help/help.coffee')
Dashboard          = standardComponent import('../Organization/dashboard.coffee')
Organization       = standardComponent import('../Organization/organization.coffee')
LongMilestoneReport= standardComponent import('../MilestoneReport/long_milestone_report.coffee')
ManageAccount      = standardComponent import('../ManageAccount/account.coffee')
GameLayout         = standardComponent import('../Game/game_layout.coffee')
OrgApplication     = standardComponent import('../Organization/application.coffee')
StudentReport      = standardComponent import('../StudentReport/student_report.coffee')
GamesList          = standardComponent import('../GameList/games.coffee')
SubjectList        = standardComponent import('../SubjectList/subjects.coffee')
Tokens             = standardComponent import('../Tokens/tokens.coffee')

requireLogin = (trans, state) ->
  unless window.app?.loopbackAPI?.isLoggedIn()
    console.log "Redirecting the user to the login page..."
    trans.router.stateService.go 'login'

globalStates = [
  # Deliberately NOT behind requireLogin — someone redeeming a reset link
  # cannot sign in, which is the entire point. Signed-in users reach the same
  # screen from Manage Account, where it asks for the current password.
  name: 'reset',
  url: '/reset',
  component: ResetPassword,
  resolve:[
    token: "none"
    resolveFn: ->
      window.app.trackPageView("2: Reset Password")
  ]
,
  name: 'login',
  url: '',
  component: Login,
  resolve:[
    token: "none"
    resolveFn: ->
      window.app.trackPageView("2: Login")
  ]
,
  name: 'application',
  url:'/application',
  onEnter: requireLogin
  component: OrgApplication,
  resolve:[
    token: "none"
    resolveFn: ->
      window.app.trackPageView("2: Application")
      if fbq?
        fbq('track', 'Lead')
  ]
,
  name: 'account'
  url:'/account?tab'
  onEnter: requireLogin
  component: ManageAccount
  resolve:[
    token: "none"
    resolveFn: ->
      window.app.trackPageView("2: Account")
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
,
  name: 'help'
  url: '/help'
  params: {url: {type: 'string', value: null}}
  redirectTo: requireLogin
  component: Help
  resolve: [
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Help")
  ]
,
  name: 'organization'
  url:'/organization'
  onEnter: requireLogin
  component: Organization
  resolve:[
    token: "organization"
    resolveFn: ->
      window.app.trackPageView("2: Organization Dashboard")
      console.log "Fetching organization with id #{window.app.login.organizationId}"
      p = window.app.loopbackAPI.Organization.findById({id: window.app.login.organizationId}, {include: accounts: 'students'})
      return p
  ]
,
  name: 'long_milestone_report'
  url: '/long_milestone_report?studentId'
  redirectTo: requireLogin
  component: LongMilestoneReport
  resolve: [
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Long Placement Exam Report")
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
,
  name: 'dashboard'
  url: '/dashboard?showIntro'
  onEnter: requireLogin
  component: Dashboard
  resolve:[
    token: "none",
    resolveFn: ->
      #console.dir window.app
      window.app.trackPageView("2: Organization Dashboard")
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
,
  name: 'games'
  url:'/games?studentId'
  onEnter: requireLogin
  component: GamesList
  resolve:[
    token: 'games'
    deps: ['$stateParams']
    resolveFn: ({studentId}) ->
      window.app.refresh() if window.app.needsRefresh()
      window.app.trackPageView("2: Game List")
      # studentId comes from $stateParams, NOT from the active student: this
      # resolve runs BEFORE digestParams latches it, so reading the active
      # student here filters the game voices for the child being navigated
      # away from (card 6aa4313530706c48305d2195).
      window.app.loadGames(studentId)

  ]
,
  name: 'subjects'
  url:'/subjects?studentId'
  redirectTo: requireLogin
  component: SubjectList
  resolve:[
    token: 'subjects'
    resolveFn: ->
      window.app.trackPageView("2: Subject List")
  ,
    token: 'refresh'
    resolveFn: ->
      if window.app.needsRefresh()
        student = window.app.getActiveStudent()
        session_duration = if student.session_duration? then student.session_duration else null
        p = window.app.refresh().then ->
          window.app.getActiveStudent().session_duration = session_duration if session_duration?
        return p
  ]
,
  name: 'student_report'
  url: '/student_report?studentId'
  params: {studentIds: {array: true, type: 'string', value: null}}
  redirectTo: requireLogin
  component: StudentReport
  resolve: [
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Student Report")
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
,
  name: 'play'
  url:'/play/:gameId/:studentId?nodeId&activityId&successAnimationId&closeOnComplete'
  onEnter: requireLogin
  component: GameLayout
  resolve:[
    token: 'games'
    deps: ['$stateParams']
    resolveFn: ({studentId}) ->
      window.app.trackPageView("2: Game Play")
      # studentId comes from $stateParams, NOT from the active student: this
      # resolve runs BEFORE digestParams latches it, so reading the active
      # student here filters the game voices for the child being navigated
      # away from (card 6aa4313530706c48305d2195).
      window.app.loadGames(studentId)
  ]
,
  name: 'playSubject'
  url:'/playsubject/:subjectId/:studentId?nodeId&activityId&successAnimationId&closeOnComplete'
  redirectTo: requireLogin
  component: GameLayout
  resolve:[
    token: 'studentRefresh',
    resolveFn: ({studentId}) ->
      console.log "Refreshing student #{studentId}..."
      window.app.loopbackAPI.Student.findById({id: studentId}, {include: {relation: 'posetProfiles', scope: {include: {relation: 'poset', scope: {fields: ['name']}}}}}).then (response) ->
        student = window.app.getStudentById(studentId)
        if student
          student.posetProfiles = response.data.posetProfiles
          window.app.dirty = true
    deps: ['$stateParams']
  ,
    token: 'games'
    deps: ['$stateParams']
    resolveFn: ({studentId}) ->
      window.app.trackPageView("2: Game Play")
      # studentId comes from $stateParams, NOT from the active student: this
      # resolve runs BEFORE digestParams latches it, so reading the active
      # student here filters the game voices for the child being navigated
      # away from (card 6aa4313530706c48305d2195).
      window.app.loadGames(studentId)
  ]
,
  name: 'tokens'
  url:'/tokens'
  onEnter: requireLogin
  component: Tokens
  resolve:[
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Login Tokens")
  ]
]

# The query can sit before the fragment (/org?access_token=..&uid=..#/dashboard)
# or inside it (/app#/login?access_token=.., GET /login's redirect), so cut the
# fragment off what follows the last '?', never off the URL: a uid that
# swallowed '#/dashboard' installed a session for no login (card 6aa6dbae617b8dc32cc6b4c0).
parseUrl = ( url = location.href ) ->
  params = {}
  ( ( parts = part.split( "=" ) ) && params[ parts[0] ] = parts[1] for part in ( url.split "?" ).pop().split( "#" )[0].split "&" if url.indexOf( "?" ) != -1 ) && params || {}

params = parseUrl window.location.href
if params.masquerade?
  clientAPI = new ClientAPI(window.config.basePath || '', "/clientapi", "$#{params.masquerade}$")
else
  clientAPI = new ClientAPI(window.config.basePath || '', "/clientapi", "$org$")

# Pre-set auth from URL params before loading schema.
# Without this, arriving with no cached token causes loadSchema() to run
# unauthenticated, returning a partial schema missing role-gated methods.
if params.access_token? && !clientAPI.isLoggedIn()
  clientAPI.setUser(params.access_token, params.uid, undefined, params.studentId)
  clientAPI.resetAuthHeader()

clientAPI.loadSchema().then () ->
  window.app = new Application(clientAPI)

  # Initialize i18n manager - loads preferred_language from database
  initI18n(clientAPI)

  # Make i18n functions globally available
  window.i18nCreateElement = i18nCreateElement
  window.t = t

  # Initialize i18n registry for string collection
  window.i18nRegistry = new I18nRegistry(clientAPI)
  # Strip query parameters from route for cleaner tracking
  routeWithoutQuery = window.location.hash.split('?')[0]
  normalizedRoute = normalizeRoute(routeWithoutQuery)
  window.i18nRegistry.setContext('elfeet/org/init', normalizedRoute, 'elfeet')

  # Always fetch all translations from server for non-English locales
  # This ensures newly imported strings are loaded, even if localStorage cache exists
  locale = window.i18nManager?.getLocale() || 'en'
  # 'en-US' is not 'en': ask the framework whether this locale reads the
  # authored text before fetching the catalogue (card 6aa6c73847c945dd8596e7d9).
  if !window.i18nManager.readsSource()
    console.log "⏳ Fetching all translations for #{locale}..."
    await clientAPI.i18nString.find({where: {app: 'elfeet'}}).then (response) ->
      cache = {}
      strings = if Array.isArray(response) then response else (response?.data || [])
      for string in strings
        cache[string.hash] = string.text

      # Save to localStorage and merge with existing cache
      try
        cacheKey = "i18n_#{locale}_elfeet"
        localStorage.setItem(cacheKey, JSON.stringify(cache))
        # Merge with any existing cache (from upsertBatch responses)
        window.i18nTranslationCache ?= {}
        for hash, text of cache
          window.i18nTranslationCache[hash] = text
        console.log "✅ Loaded #{Object.keys(cache).length} translations from server"
      catch e
        console.warn "Failed to cache translations:", e

  hide_agent_widget() if window.app.isApp()

  configure = (router) ->
    router.urlRouter.otherwise {state: 'teacherDashboard'}

    # Update i18n context on route transitions
    router.transitionService.onSuccess {}, (transition) ->
      toState = transition.to()
      # Strip query parameters from route for cleaner tracking
      routeWithoutQuery = window.location.hash.split('?')[0]
      normalizedRoute = normalizeRoute(routeWithoutQuery)
      window.i18nRegistry?.setContext(
        "elfeet/org/#{toState.name}",
        normalizedRoute,
        'elfeet'
      )

  start_react = ->
    createRoot(document.getElementById("root")).render(
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <TranslationProvider route={window.location.hash} app="elfeet">
            <UIRouter plugins={[hashLocationPlugin]} states={globalStates} config={configure}>
              <div style={{height: "100%"}}>
                <GlobalToast/>
                <UIView/>
              </div>
            </UIRouter>
          </TranslationProvider>
        </ErrorBoundary>
      </ThemeProvider>
    )

  initialize = (route_to='dashboard', route_params={}) ->
    console.log "Initialize Called route to is #{route_to}"
    window.application_running = 'org'
    window.app.refresh().then (response) ->
      #console.log "INITIALIZE ----------- Response:"
      #console.dir response
      if response.data?.login?
        # Re-initialize i18n with the user's actual preferred language now that we have login data
        preferredLang = response.data.login.preferredLanguage || response.data.login.preferred_language || 'en'
        # 'en-US' is not 'en': ask the framework whether this locale reads the
        # authored text before fetching the catalogue (card 6aa6c73847c945dd8596e7d9).
        if !window.i18nManager.readsSource(preferredLang)
          initI18n(clientAPI, preferredLang)

          # Set moment locale for date formatting and month names
          momentLang = preferredLang.split('-')[0].toLowerCase()
          import("moment/locale/#{momentLang}")
            .then -> moment.locale(momentLang)
            .catch -> console.warn "moment locale #{momentLang} not available"
          await clientAPI.i18nString.find({where: {app: 'elfeet'}}).then (resp) ->
            cache = {}
            strings = if Array.isArray(resp) then resp else (resp?.data || [])
            for string in strings
              cache[string.hash] = string.text
            try
              localStorage.setItem("i18n_#{preferredLang}_elfeet", JSON.stringify(cache))
              window.i18nTranslationCache ?= {}
              for hash, text of cache
                window.i18nTranslationCache[hash] = text
            catch e
              console.warn "Failed to cache org translations:", e
      if response.data?.login? # we did not fetch account data, send them to login screen...
        account_type = response.data.login.account.type
        if !account_type? || !(account_type in ['organization', 'district', 'school'])
          window.location = '/'
        else if !response.data.login?.organization?.zip? || !response.data.login?.organization?.name? || response.data.login?.organization?.name == "" || response.data.login?.organization?.zip == ""
          console.log "Routing to Application here is organizaiton data:"
          console.dir response.data.login.organization
          window.location = '#/application'
        else if moment(response.data.login.account.subscription_end).isBefore(moment())
          console.log "Route to account"
          window.location = '#/account?tab=1'
        else
          locString = "#/#{route_to}"
          c = '?'
          for key, value of route_params
            locString += "#{c}#{key}=#{value}"
            c = '&'
          window.location = locString
        start_react()
      else
        window.location.hash = ''

  window.teacher_mode = true
  window.TitleBar = TitleBar
  # Strip the sign-in credentials out of the address bar once they have been
  # consumed — the same two replaceState calls TeacherPortal/app-route.coffee
  # already makes. Without them /org kept ?access_token=LoginAuth_…&uid=… for the
  # whole session, so a school admin's live session token rode along into their
  # bookmarks, their history, every screenshot they sent support, and the
  # Referer of every outbound link (card 6aa4d4091c031a8daf2e49cb).
  if params.access_token? && params.uid? && window.app.loopbackAPI.isLoggedIn()
    window.app.loopbackAPI.clearUser() if window.app.loopbackAPI.currentUserId != params.uid
    # Keep the hash: it is the screen the admin was sent to (#/dashboard), and
    # only the credentials are being removed. The teacher bootstrap drops it
    # because its own initialize() re-derives the route straight afterwards;
    # here the branch below reads window.location.hash to decide where to go.
    window.history.replaceState {}, document.title, "/org#{window.location.hash}"

  if window.app.loopbackAPI.isLoggedIn() && !(params.new_user == 'true' && params.access_token?)
    if !window.location.hash? || window.location.hash is '' || window.location.hash is '#/'
      initialize()
    else
      route = window.location.hash.substring 2
      initialize route
  else if params.access_token?
    window.app.loopbackAPI.setUser(params.access_token, params.uid, undefined, params.studentId)
    window.app.loopbackAPI.resetAuthHeader()
    window.history.replaceState {}, document.title, "/org#{window.location.hash}"

    # to avoid variable capture, redirectParams are passed as a separate hash
    redirectParams = {}
    try
      if params.redirectParams?
        redirectParams = JSON.parse(params.redirectParams)
    catch
      console.error "Ignoring bad redirect params: #{params.redirectParams}"

    initialize params.redirectTo, params.redirectParams
  else if window.location.hash != '' and not isUnauthenticatedDeepLink(window.location.hash)
    # See the matching note in TeacherPortal/app-route.coffee: the reset link's
    # audience is precisely the people who cannot sign in, so its hash must
    # survive the logged-out wipe. Card 6a639e22893d0bbbaaacec81.
    window.location.hash = ''
    start_react()
  else
    start_react()
